import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Badge, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBuilding, 
  faCreditCard, 
  faBell,
  faShield,
  faEdit,
  faSave,
  faCheck,
  faRocket,
  faGlobe,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import BrokerApiService from '../../services/brokerApiService';

interface BrokerSettingsProps {
  brokerageId?: string;
  data?: any;
}

interface SettingsData {
  company: {
    name: string;
    planType: string;
    officeAddress: string;
    phone: string;
    email: string;
    mlsOfficeId: string;
    associationId: string;
    maxAgents: number;
    currentAgents: number;
  };
  billing: {
    paymentMethod: string;
    billingEmail: string;
    autoRenew: boolean;
    invoiceDelivery: string;
  };
  notifications: {
    agentActivity: boolean;
    billingAlerts: boolean;
    systemUpdates: boolean;
    weeklyReports: boolean;
    planUpgrades: boolean;
  };
  security: {
    twoFactorAuth: boolean;
    ssoEnabled: boolean;
    apiAccess: boolean;
  };
}

type EditableSection = 'company' | 'billing' | 'notifications' | 'security';

const COMPANY_PLACEHOLDERS = {
  name: 'Your Brokerage',
  officeAddress: 'Enter your office address',
  phone: 'Enter phone number',
  email: 'Enter email address',
  mlsOfficeId: 'Enter MLS Office ID'
};

type CompanyPlaceholderField = keyof typeof COMPANY_PLACEHOLDERS;

const createDefaultSettings = (): SettingsData => ({
  company: {
    name: COMPANY_PLACEHOLDERS.name,
    planType: 'team',
    officeAddress: COMPANY_PLACEHOLDERS.officeAddress,
    phone: COMPANY_PLACEHOLDERS.phone,
    email: COMPANY_PLACEHOLDERS.email,
    mlsOfficeId: COMPANY_PLACEHOLDERS.mlsOfficeId,
    associationId: '',
    maxAgents: 50,
    currentAgents: 0
  },
  billing: {
    paymentMethod: 'No payment method on file',
    billingEmail: 'Enter billing email',
    autoRenew: false,
    invoiceDelivery: 'email'
  },
  notifications: {
    agentActivity: true,
    billingAlerts: true,
    systemUpdates: true,
    weeklyReports: true,
    planUpgrades: true
  },
  security: {
    twoFactorAuth: false,
    ssoEnabled: false,
    apiAccess: false
  }
});

interface AssociationRecord {
  id: number;
  association_id?: string | number;
  association_name: string;
  city?: string;
  state?: string;
}

const BrokerSettings: React.FC<BrokerSettingsProps> = ({ 
  brokerageId, 
  data 
}) => {
  const [settingsData, setSettingsData] = useState<SettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<Record<EditableSection, boolean>>({
    company: false,
    billing: false,
    notifications: false,
    security: false
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [associations, setAssociations] = useState<AssociationRecord[]>([]);
  const [associationsLoading, setAssociationsLoading] = useState(false);
  const [associationsError, setAssociationsError] = useState<string | null>(null);
  const [companyProfileLoading, setCompanyProfileLoading] = useState(false);
  const [companyProfileBanner, setCompanyProfileBanner] = useState<{ variant: 'info' | 'warning'; message: string } | null>(null);
  const [savingSection, setSavingSection] = useState<EditableSection | null>(null);

  const clearCompanyPlaceholder = useCallback((field: CompanyPlaceholderField) => {
    setSettingsData(prev => {
      if (!prev) return prev;
      if (prev.company[field] === COMPANY_PLACEHOLDERS[field]) {
        return {
          ...prev,
          company: {
            ...prev.company,
            [field]: ''
          }
        };
      }
      return prev;
    });
  }, []);

  const restoreCompanyPlaceholder = useCallback((field: CompanyPlaceholderField) => {
    setSettingsData(prev => {
      if (!prev) return prev;
      const value = prev.company[field];
      if (!value || !value.trim()) {
        return {
          ...prev,
          company: {
            ...prev.company,
            [field]: COMPANY_PLACEHOLDERS[field]
          }
        };
      }
      return prev;
    });
  }, []);

  const applyCompanyProfile = useCallback((profile: any) => {
    if (!profile) {
      return;
    }

    setSettingsData(prev => {
      const base = prev ?? createDefaultSettings();
      return {
        ...base,
        company: {
          ...base.company,
          name: profile.company_name || base.company.name,
          officeAddress: profile.company_address || base.company.officeAddress,
          phone: profile.company_phone || base.company.phone,
          email: profile.company_email || base.company.email,
          mlsOfficeId: profile.mls_id || base.company.mlsOfficeId,
          associationId: profile.association_id
            ? profile.association_id.toString()
            : base.company.associationId
        }
      };
    });
  }, []);

  const fetchCompanyProfile = useCallback(async () => {
    try {
      setCompanyProfileLoading(true);
      setCompanyProfileBanner(null);

      const response = await BrokerApiService.getCompanyProfile();
      const data = response.data as any;

      if (response.success && data && data.company_name !== undefined) {
        applyCompanyProfile(data);
        setCompanyProfileBanner(null);
      } else if (!response.success) {
        const friendlyMessage = 'No brokerage details saved yet. Complete the form below to get started.';
        setCompanyProfileBanner({ variant: 'info', message: friendlyMessage });
      }
    } catch (err: any) {
      console.warn('⚠️ Unable to load company profile:', err);
      setCompanyProfileBanner({
        variant: 'warning',
        message: 'We could not load your saved brokerage info yet. Update the fields below and click Save to continue.'
      });
    } finally {
      setCompanyProfileLoading(false);
    }
  }, [applyCompanyProfile]);

  // Fetch settings data from API
  useEffect(() => {
    const fetchSettingsData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!brokerageId) {
          setError('No brokerage ID provided');
          setLoading(false);
          return;
        }

        const response = await BrokerApiService.getBillingInfo(brokerageId);
        
        if (response.success) {
          const billing = response.billing;
          const agentCounts = billing.current_month.agent_counts;
          
          setSettingsData(prev => {
            const base = prev ?? createDefaultSettings();
            return {
              ...base,
              company: {
                ...base.company,
                planType: billing.plan_type || base.company.planType,
                maxAgents: billing.max_agents || base.company.maxAgents,
                currentAgents: agentCounts.total ?? base.company.currentAgents
              },
              billing: {
                ...base.billing,
                paymentMethod: billing.payment_method || base.billing.paymentMethod
              }
            };
          });
        } else {
          setError('Failed to load settings data');
        }
      } catch (err) {
        console.error('Error fetching settings data:', err);
        setError('Unable to connect to server - API endpoint may not be available');
      } finally {
        setLoading(false);
      }
    };

    fetchSettingsData();
  }, [brokerageId]);

  useEffect(() => {
    fetchCompanyProfile();
  }, [brokerageId, fetchCompanyProfile]);

  useEffect(() => {
    let isMounted = true;
    const fetchAssociations = async () => {
      try {
        setAssociationsLoading(true);
        setAssociationsError(null);

        const associationApiUrl = process.env.NODE_ENV === 'production'
          ? '/api/associations'
          : 'http://localhost:8080/api/associations';

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(associationApiUrl, { signal: controller.signal });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Failed to load associations: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.success && data.data && data.data.associations) {
          if (isMounted) {
            setAssociations(data.data.associations);
          }
        } else {
          throw new Error(data.message || 'Invalid association response');
        }
      } catch (err: any) {
        if (!isMounted) return;
        const message = err?.name === 'AbortError'
          ? 'Association lookup timed out. Try again in a moment.'
          : err?.message || 'Unable to load associations';
        setAssociationsError(message);
        console.error('🔁 [BrokerSettings] Error loading associations:', err);
      } finally {
        if (isMounted) {
          setAssociationsLoading(false);
        }
      }
    };

    fetchAssociations();
    return () => { isMounted = false; };
  }, []);

  const saveCompanySettings = async () => {
    if (!settingsData) {
      throw new Error('Settings not loaded yet');
    }

    const sanitizeField = (value: string, placeholder?: string) => {
      if (!value) return '';
      if (placeholder && value === placeholder) return '';
      return value.trim();
    };

    const associationValue = sanitizeField(settingsData.company.associationId || '');
    const normalizedAssociationId = associationValue
      ? (/^\d+$/.test(associationValue) ? Number(associationValue) : associationValue)
      : null;

    const payload = {
      company_name: sanitizeField(settingsData.company.name, COMPANY_PLACEHOLDERS.name),
      company_address: sanitizeField(settingsData.company.officeAddress, COMPANY_PLACEHOLDERS.officeAddress),
      company_phone: sanitizeField(settingsData.company.phone, COMPANY_PLACEHOLDERS.phone),
      company_email: sanitizeField(settingsData.company.email, COMPANY_PLACEHOLDERS.email),
      license_number: '',
      mls_id: sanitizeField(settingsData.company.mlsOfficeId, COMPANY_PLACEHOLDERS.mlsOfficeId),
      office_short_id: '',
      mls_access: Boolean(sanitizeField(settingsData.company.mlsOfficeId, COMPANY_PLACEHOLDERS.mlsOfficeId)),
      association_id: normalizedAssociationId,
      mls_login_id: '',
      agreed_to_terms: true
    };

    const response = await BrokerApiService.saveCompanyProfile(payload);
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to save company profile');
    }

    const responseData: any = response.data;
    if (responseData && responseData.brokerage_id) {
      localStorage.setItem('brokerageId', responseData.brokerage_id.toString());
    }
    if (settingsData.company.name) {
      localStorage.setItem('brokerageName', settingsData.company.name);
    }

    await fetchCompanyProfile();
  };

  const handleSave = async (section: EditableSection) => {
    try {
      setError(null);
      setSavingSection(section);
      if (section === 'company') {
        await saveCompanySettings();
      }
      setIsEditing(prev => ({ ...prev, [section]: false }));
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError(err?.message || 'Failed to save settings');
    } finally {
      setSavingSection(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading settings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-4">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        {error}
      </Alert>
    );
  }

  if (!settingsData) {
    return (
      <Alert variant="warning" className="m-4">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        No settings data available
      </Alert>
    );
  }

  const renderCompanySettingsCard = () => (
    <Card className="h-100 border-0 ai-enhanced-card">
      <Card.Header 
        className="border-bottom-0 py-4"
        style={{ 
          background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(14, 165, 233, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px 16px 0 0'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={faBuilding} 
              className="me-2"
              style={{ color: '#00d4ff', fontSize: '20px' }}
            />
            <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
              Company Information
            </h5>
          </div>
          <Button 
            variant={isEditing.company ? 'success' : 'outline-primary'} 
            size="sm"
            disabled={savingSection === 'company'}
            onClick={() => isEditing.company 
              ? void handleSave('company') 
              : setIsEditing(prev => ({ ...prev, company: true }))}
          >
            {isEditing.company ? (
              savingSection === 'company' ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving…
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} className="me-1" />
                  Save
                </>
              )
            ) : (
              <>
                <FontAwesomeIcon icon={faEdit} className="me-1" />
                Edit
              </>
            )}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {(companyProfileLoading || companyProfileBanner) && (
          <Alert 
            variant={companyProfileBanner?.variant || 'info'} 
            className="d-flex align-items-center justify-content-between mb-3"
          >
            <div className="d-flex align-items-center">
              {companyProfileLoading && (
                <Spinner animation="border" size="sm" className="me-2" />
              )}
              <span>
                {companyProfileLoading
                  ? 'Syncing saved brokerage details...'
                  : companyProfileBanner?.message}
              </span>
            </div>
            {!companyProfileLoading && companyProfileBanner?.variant === 'warning' && (
              <Button 
                variant="outline-warning" 
                size="sm"
                onClick={() => fetchCompanyProfile()}
              >
                Retry
              </Button>
            )}
          </Alert>
        )}
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Company Name</Form.Label>
                <Form.Control 
                  type="text" 
                  value={settingsData.company.name}
                  disabled={!isEditing.company}
                  onFocus={() => clearCompanyPlaceholder('name')}
                  onBlur={() => restoreCompanyPlaceholder('name')}
                  onChange={(e) => setSettingsData(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      company: { ...prev.company, name: e.target.value }
                    };
                  })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Plan Type</Form.Label>
                <div className="d-flex align-items-center">
                  <Badge 
                    bg="primary" 
                    className="me-2"
                    style={{ fontSize: '12px' }}
                  >
                    <FontAwesomeIcon icon={faRocket} className="me-1" />
                    {settingsData.company.planType.toUpperCase()}
                  </Badge>
                  <small className="text-muted">
                    {settingsData.company.currentAgents}/{settingsData.company.maxAgents} agents
                  </small>
                </div>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">Office Address</Form.Label>
                <Form.Control 
                  as="textarea"
                  rows={2}
                  value={settingsData.company.officeAddress}
                  disabled={!isEditing.company}
                  onFocus={() => clearCompanyPlaceholder('officeAddress')}
                  onBlur={() => restoreCompanyPlaceholder('officeAddress')}
                  onChange={(e) => setSettingsData(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      company: { ...prev.company, officeAddress: e.target.value }
                    };
                  })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Phone Number</Form.Label>
                <Form.Control 
                  type="tel" 
                  value={settingsData.company.phone}
                  disabled={!isEditing.company}
                  onFocus={() => clearCompanyPlaceholder('phone')}
                  onBlur={() => restoreCompanyPlaceholder('phone')}
                  onChange={(e) => setSettingsData(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      company: { ...prev.company, phone: e.target.value }
                    };
                  })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Email Address</Form.Label>
                <Form.Control 
                  type="email" 
                  value={settingsData.company.email}
                  disabled={!isEditing.company}
                  onFocus={() => clearCompanyPlaceholder('email')}
                  onBlur={() => restoreCompanyPlaceholder('email')}
                  onChange={(e) => setSettingsData(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      company: { ...prev.company, email: e.target.value }
                    };
                  })}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">MLS Office ID</Form.Label>
                <Form.Control 
                  type="text" 
                  value={settingsData.company.mlsOfficeId}
                  disabled={!isEditing.company}
                  onFocus={() => clearCompanyPlaceholder('mlsOfficeId')}
                  onBlur={() => restoreCompanyPlaceholder('mlsOfficeId')}
                  onChange={(e) => setSettingsData(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      company: { ...prev.company, mlsOfficeId: e.target.value }
                    };
                  })}
                />
                <Form.Text className="text-muted">
                  Used for automatic agent discovery and bulk imports
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">MLS Association</Form.Label>
                <Form.Select
                  value={settingsData.company.associationId}
                  disabled={!isEditing.company || associations.length === 0 || associationsLoading}
                  onChange={(e) => setSettingsData(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      company: { ...prev.company, associationId: e.target.value }
                    };
                  })}
                >
                  <option value="">
                    {associations.length === 0
                      ? 'No associations available'
                      : 'Select your MLS association'}
                  </option>
                  {associations
                    .filter(assoc => assoc.association_name)
                    .sort((a, b) => a.association_name.localeCompare(b.association_name))
                    .map(association => {
                      const optionValue = association.association_id ?? association.id;
                      const locationSuffix = association.city || association.state
                        ? ` (${[association.city, association.state].filter(Boolean).join(', ')})`
                        : '';
                      return (
                        <option 
                          key={`${association.id}-${association.association_id ?? ''}`}
                          value={optionValue?.toString()}
                        >
                          {association.association_name}{locationSuffix}
                        </option>
                      );
                    })}
                </Form.Select>
                {associationsLoading && (
                  <Form.Text className="text-muted d-flex align-items-center mt-2">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading MLS association directory…
                  </Form.Text>
                )}
                {associationsError ? (
                  <Form.Text className="text-danger d-block mt-2">
                    {associationsError}
                  </Form.Text>
                ) : (
                  <Form.Text className="text-muted d-block mt-2">
                    Matches the association list used in agent profile settings so brokers stay in sync with agents.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );

  const renderBillingSettingsCard = () => (
    <Card className="h-100 border-0 ai-enhanced-card">
      <Card.Header 
        className="border-bottom-0 py-4"
        style={{ 
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px 16px 0 0'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={faCreditCard} 
              className="me-2"
              style={{ color: '#10b981', fontSize: '20px' }}
            />
            <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
              Billing & Payment
            </h5>
          </div>
          <Button 
            variant={isEditing.billing ? 'success' : 'outline-primary'} 
            size="sm"
            disabled={savingSection === 'billing'}
            onClick={() => isEditing.billing ? void handleSave('billing') : setIsEditing(prev => ({ ...prev, billing: true }))}
          >
            <FontAwesomeIcon 
              icon={isEditing.billing ? faSave : faEdit} 
              className="me-1" 
            />
            {isEditing.billing ? 'Save' : 'Edit'}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <Form>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">Payment Method</Form.Label>
                <div className="d-flex align-items-center justify-content-between p-3 rounded-3" 
                     style={{ background: 'rgba(16, 185, 129, 0.05)' }}>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faCreditCard} className="me-2 text-success" />
                    <span>{settingsData.billing.paymentMethod}</span>
                  </div>
                  {isEditing.billing && (
                    <Button variant="outline-primary" size="sm">
                      Update
                    </Button>
                  )}
                </div>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">Billing Email</Form.Label>
                <Form.Control 
                  type="email" 
                  value={settingsData.billing.billingEmail}
                  disabled={!isEditing.billing}
                  onChange={(e) => setSettingsData(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      billing: { ...prev.billing, billingEmail: e.target.value }
                    };
                  })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Check 
                  type="switch"
                  id="auto-renew"
                  label="Auto-renewal"
                  checked={settingsData.billing.autoRenew}
                  disabled={!isEditing.billing}
                  onChange={(e) => setSettingsData(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      billing: { ...prev.billing, autoRenew: e.target.checked }
                    };
                  })}
                />
                <Form.Text className="text-muted">
                  Automatically renew subscription monthly
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Invoice Delivery</Form.Label>
                <Form.Select 
                  value={settingsData.billing.invoiceDelivery}
                  disabled={!isEditing.billing}
                  onChange={(e) => setSettingsData(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      billing: { ...prev.billing, invoiceDelivery: e.target.value }
                    };
                  })}
                >
                  <option value="email">Email</option>
                  <option value="postal">Postal Mail</option>
                  <option value="both">Both</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );

  const renderNotificationSettingsCard = () => (
    <Card className="h-100 border-0 ai-enhanced-card">
      <Card.Header 
        className="border-bottom-0 py-4"
        style={{ 
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px 16px 0 0'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={faBell} 
              className="me-2"
              style={{ color: '#f59e0b', fontSize: '20px' }}
            />
            <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
              Notifications
            </h5>
          </div>
          <Button 
            variant={isEditing.notifications ? 'success' : 'outline-primary'} 
            size="sm"
            disabled={savingSection === 'notifications'}
            onClick={() => isEditing.notifications ? void handleSave('notifications') : setIsEditing(prev => ({ ...prev, notifications: true }))}
          >
            <FontAwesomeIcon 
              icon={isEditing.notifications ? faSave : faEdit} 
              className="me-1" 
            />
            {isEditing.notifications ? 'Save' : 'Edit'}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="space-y-3">
          {Object.entries(settingsData.notifications).map(([key, value]: [string, any]) => (
            <div key={key} className="d-flex justify-content-between align-items-center p-3 rounded-3" 
                 style={{ background: 'rgba(245, 158, 11, 0.05)' }}>
              <div>
                <div className="fw-semibold text-capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </div>
                <small className="text-muted">
                  {key === 'agentActivity' && 'Get notified about agent logins and activity'}
                  {key === 'billingAlerts' && 'Receive billing and payment notifications'}
                  {key === 'systemUpdates' && 'Stay informed about platform updates'}
                  {key === 'weeklyReports' && 'Receive weekly performance summaries'}
                  {key === 'planUpgrades' && 'Get notified when agents upgrade plans'}
                </small>
              </div>
              <Form.Check 
                type="switch"
                id={`notification-${key}`}
                checked={value}
                disabled={!isEditing.notifications}
                onChange={(e) => setSettingsData(prev => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    notifications: { 
                      ...prev.notifications, 
                      [key]: e.target.checked 
                    }
                  };
                })}
              />
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );

  const renderSecuritySettingsCard = () => (
    <Card className="h-100 border-0 ai-enhanced-card">
      <Card.Header 
        className="border-bottom-0 py-4"
        style={{ 
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px 16px 0 0'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={faShield} 
              className="me-2"
              style={{ color: '#ef4444', fontSize: '20px' }}
            />
            <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
              Security & Access
            </h5>
          </div>
          <Button 
            variant={isEditing.security ? 'success' : 'outline-primary'} 
            size="sm"
            disabled={savingSection === 'security'}
            onClick={() => isEditing.security ? void handleSave('security') : setIsEditing(prev => ({ ...prev, security: true }))}
          >
            <FontAwesomeIcon 
              icon={isEditing.security ? faSave : faEdit} 
              className="me-1" 
            />
            {isEditing.security ? 'Save' : 'Edit'}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="space-y-3">
          <div className="d-flex justify-content-between align-items-center p-3 rounded-3" 
               style={{ background: 'rgba(239, 68, 68, 0.05)' }}>
            <div>
              <div className="fw-semibold">Two-Factor Authentication</div>
              <small className="text-muted">Add extra security to your account</small>
            </div>
            <Form.Check 
              type="switch"
              id="two-factor"
              checked={settingsData.security.twoFactorAuth}
              disabled={!isEditing.security}
              onChange={(e) => setSettingsData(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  security: { ...prev.security, twoFactorAuth: e.target.checked }
                };
              })}
            />
          </div>

          <div className="d-flex justify-content-between align-items-center p-3 rounded-3" 
               style={{ background: 'rgba(239, 68, 68, 0.05)' }}>
            <div>
              <div className="fw-semibold">Single Sign-On (SSO)</div>
              <small className="text-muted">Enable SSO for your organization</small>
            </div>
            <div className="d-flex align-items-center">
              <Badge bg="warning" className="me-2" style={{ fontSize: '10px' }}>
                Enterprise Only
              </Badge>
              <Form.Check 
                type="switch"
                id="sso"
                checked={settingsData.security.ssoEnabled}
                disabled={true}
              />
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center p-3 rounded-3" 
               style={{ background: 'rgba(239, 68, 68, 0.05)' }}>
            <div>
              <div className="fw-semibold">API Access</div>
              <small className="text-muted">Enable API access for integrations</small>
            </div>
            <Form.Check 
              type="switch"
              id="api-access"
              checked={settingsData.security.apiAccess}
              disabled={!isEditing.security}
              onChange={(e) => setSettingsData(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  security: { ...prev.security, apiAccess: e.target.checked }
                };
              })}
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <div className="broker-settings">
      {/* AI Intelligence Header */}
      <Card 
        className="border-0 mb-4"
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Card.Body className="p-4">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center me-4"
                style={{ 
                  width: "56px", 
                  height: "56px",
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  boxShadow: '0 0 25px rgba(99, 102, 241, 0.5)'
                }}
              >
                <FontAwesomeIcon 
                  icon={faGlobe} 
                  className="text-white" 
                  size="lg"
                  style={{ filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.5))' }}
                />
              </div>
              <div>
                <h3 className="mb-1 fw-bold" style={{ 
                  color: '#1f2937',
                  textShadow: '0 0 10px rgba(99, 102, 241, 0.3)'
                }}>
                  Brokerage Settings
                </h3>
                <p className="mb-0 text-muted">
                  Configure your brokerage profile and preferences
                </p>
              </div>
            </div>
            
            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              padding: '12px 20px',
              borderRadius: '25px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}>
              <div className="d-flex align-items-center">
                <div className="rounded-circle me-2" style={{
                  width: '10px', 
                  height: '10px', 
                  background: '#10b981',
                  animation: 'pulse 2s infinite'
                }} />
                <span className="fw-semibold text-success" style={{ 
                  fontSize: '14px', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px'
                }}>
                  <FontAwesomeIcon icon={faRocket} className="me-1" />
                  SETTINGS SYNCED
                </span>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Success Alert */}
      {showSuccess && (
        <Alert variant="success" className="d-flex align-items-center mb-4">
          <FontAwesomeIcon icon={faCheck} className="me-2" />
          Settings saved successfully!
        </Alert>
      )}

      {/* Settings Cards */}
      <Row className="g-4">
        <Col lg={6}>
          {renderCompanySettingsCard()}
        </Col>
        <Col lg={6}>
          {renderBillingSettingsCard()}
        </Col>
        <Col lg={6}>
          {renderNotificationSettingsCard()}
        </Col>
        <Col lg={6}>
          {renderSecuritySettingsCard()}
        </Col>
      </Row>
    </div>
  );
};

export default BrokerSettings;
